import { Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { catchError, switchMap, tap, throwError } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { TherapistApiService } from '@therapist/shared/services/api/therapist-api.service';
import { TherapistStateService } from '@therapist/shared/services/state/therapist-state.service';
import { TherapistAuthStateService } from '@therapist/shared/services/state/therapist-auth-state.service';
import { TherapistDto } from '@therapist/shared/models/therapist/therapist.dto';

@Component({
  selector: 'sana-tp-password-reset',
  imports: [
    ReactiveFormsModule,
    RouterLink
  ],
  templateUrl: './password-reset.component.html',
  styleUrl: './password-reset.component.scss'
})
export class PasswordResetComponent {

  readonly #fb = inject(FormBuilder);
  readonly #router = inject(Router);
  readonly #activatedRoute = inject(ActivatedRoute);

  readonly #therapistApiService = inject(TherapistApiService);
  readonly #therapistStateService = inject(TherapistStateService);

  readonly #authStateService = inject(TherapistAuthStateService);

  isSubmitting = signal(false);
  showPassword = signal(false);
  errorMessage: string | null = null;

  readonly currentYear = new Date().getFullYear();

  token: string | null = null;

  passwordResetForm = this.#fb.group({
    newPassword: [
      null,
      [
        Validators.required,
        Validators.minLength(8)
      ]
    ]
  });

  constructor() {
    const route = this.#activatedRoute.snapshot;

    if (!route.fragment) {
      console.warn('No  token present in url');
    }

    const token = this.extractTokenFromFragment(route.fragment!);

    if (token === null) {
      console.warn('Could not parse token from url');
    }

    this.token = token;
  }

  togglePassword(): void {
    this.showPassword.update(value => !value);
  }

  onSubmit(): void {
    this.passwordResetForm.markAllAsTouched();

    if (this.passwordResetForm.invalid) {
      return;
    }

    this.isSubmitting.set(true);

    this.#therapistApiService
      .resetPassword(this.token!, this.passwordResetForm.controls.newPassword.value!)
      .pipe(
        // 1) Nach Login zuerst Token speichern
        tap(login => this.#authStateService.setAccessToken(login.token)),

        // 2) Dann mit gültigem Token das Profil laden
        switchMap(login => this.#therapistApiService.getProfile()),

        // 3) Fehlerbehandlung
        catchError((err: HttpErrorResponse) => {
          this.handleLoginError(err);
          return throwError(() => err);
        })
      )
      .subscribe({
        next: therapist => this.handleLoginSuccess(therapist),
        error: err => {
          this.handleLoginError(err);
          return throwError(() => err);
        }
      });
  }

  /**
   * Erwartet z. B. "#token=ABC.DEF" und extrahiert den Token aus der URL
   * unterstützt auch mehrere Paare wie "#token=...&x=..."
   */
  private extractTokenFromFragment(fragment: string): string | null {
    const params = new URLSearchParams(fragment?.replace(/^#/, '') ?? '');
    let token = params.get('token');

    if (!token) {
      return null;
    }

    token = token.replace('$', '');
    return token && token.trim().length > 0 ? token : null;
  }

  private handleLoginSuccess(therapist: TherapistDto) {
    this.#therapistStateService.setCurrentTherapist(therapist);
    this.#router.navigate(['/']);
    this.isSubmitting.set(false);
  }

  private handleLoginError(err: HttpErrorResponse) {
    this.errorMessage = 'Das Zurücksetzen des Passworts ist fehlgeschlagen. Bitte versuchen Sie es erneut.';
    this.isSubmitting.set(false);
  }
}
